import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { createProject, updateProject, getProject, getCategories } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Calendar as CalendarIcon, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const EVENT_CATEGORIES = [
  { value: 'Wedding Planner', label: 'Wedding Planner', description: 'Client view-only, Leader manages all data' },
  { value: 'Wedding Organizer', label: 'Wedding Organizer', description: 'Client can add/edit items' },
  { value: 'Sangjit', label: 'Sangjit', description: 'Client can add/edit items' }
];

const PROJECT_STATUSES = [
  { value: 0, label: 'Active' },
  { value: 1, label: 'Finish' },
  { value: 2, label: 'Pending' }
];

export const ProjectFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isLeader } = useAuth();
  const isEdit = !!id;
  
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [date, setDate] = useState(null);
  const [formData, setFormData] = useState({
    bride_name: '',
    groom_name: '',
    bride_email: '',
    groom_email: '',
    wedding_date: '',
    location: '',
    target_budget: '',
    event_category: 'Wedding Planner',
    status: 0,
    notes: ''
  });

  useEffect(() => {
    if (!isLeader()) {
      navigate('/dashboard');
      return;
    }
    if (isEdit) {
      fetchProject();
    }
  }, [id, isLeader, navigate, isEdit]);

  const fetchProject = async () => {
    try {
      const response = await getProject(id);
      const project = response.data;
      setFormData({
        bride_name: project.bride_name,
        groom_name: project.groom_name,
        bride_email: project.bride_email,
        groom_email: project.groom_email,
        wedding_date: project.wedding_date,
        location: project.location,
        target_budget: project.target_budget.toString(),
        event_category: project.event_category || 'Wedding Planner',
        status: project.status !== undefined ? project.status : 0,
        notes: project.notes || ''
      });
      if (project.wedding_date) {
        setDate(new Date(project.wedding_date));
      }
    } catch (error) {
      toast.error('Failed to fetch project');
      navigate('/projects');
    } finally {
      setFetching(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.bride_name || !formData.groom_name || !formData.bride_email || 
        !formData.groom_email || !formData.wedding_date || !formData.location || !formData.target_budget) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const data = {
        ...formData,
        target_budget: parseFloat(formData.target_budget),
        status: parseInt(formData.status)
      };
      
      if (isEdit) {
        await updateProject(id, data);
        toast.success('Project updated successfully');
      } else {
        await createProject(data);
        toast.success('Project created! Welcome emails sent to bride & groom.');
      }
      navigate('/projects');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save project');
    } finally {
      setLoading(false);
    }
  };

  const handleDateSelect = (selectedDate) => {
    setDate(selectedDate);
    if (selectedDate) {
      setFormData({ ...formData, wedding_date: format(selectedDate, 'yyyy-MM-dd') });
    }
  };

  if (fetching) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-imperial" />
      </div>
    );
  }

  return (
    <div data-testid="project-form-page" className="max-w-3xl mx-auto space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => navigate('/projects')}
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="font-heading text-3xl font-bold text-imperial">
            {isEdit ? 'Edit Project' : 'New Wedding Project'}
          </h1>
          <p className="font-body text-muted-foreground mt-1">
            {isEdit ? 'Update project details' : 'Create a new wedding project and onboard clients'}
          </p>
        </div>
      </div>

      {/* Form */}
      <Card className="card-luxury">
        <CardHeader>
          <CardTitle className="font-heading text-xl">Project Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Bride & Groom */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="font-body text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Bride Name *
                </Label>
                <Input
                  value={formData.bride_name}
                  onChange={(e) => setFormData({ ...formData, bride_name: e.target.value })}
                  placeholder="Enter bride's name"
                  className="h-12 bg-white border-warm-grey"
                  data-testid="bride-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-body text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Groom Name *
                </Label>
                <Input
                  value={formData.groom_name}
                  onChange={(e) => setFormData({ ...formData, groom_name: e.target.value })}
                  placeholder="Enter groom's name"
                  className="h-12 bg-white border-warm-grey"
                  data-testid="groom-name-input"
                />
              </div>
            </div>

            {/* Emails */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="font-body text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Bride Email *
                </Label>
                <Input
                  type="email"
                  value={formData.bride_email}
                  onChange={(e) => setFormData({ ...formData, bride_email: e.target.value })}
                  placeholder="bride@email.com"
                  className="h-12 bg-white border-warm-grey"
                  data-testid="bride-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-body text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Groom Email *
                </Label>
                <Input
                  type="email"
                  value={formData.groom_email}
                  onChange={(e) => setFormData({ ...formData, groom_email: e.target.value })}
                  placeholder="groom@email.com"
                  className="h-12 bg-white border-warm-grey"
                  data-testid="groom-email-input"
                />
              </div>
            </div>

            {/* Date & Location */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="font-body text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Wedding Date *
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full h-12 justify-start text-left font-normal bg-white border-warm-grey"
                      data-testid="wedding-date-button"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 text-muted-foreground" />
                      {date ? format(date, 'PPP') : <span className="text-muted-foreground">Select date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={handleDateSelect}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label className="font-body text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Location *
                </Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="Wedding venue location"
                  className="h-12 bg-white border-warm-grey"
                  data-testid="location-input"
                />
              </div>
            </div>

            {/* Budget */}
            <div className="space-y-2">
              <Label className="font-body text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Target Budget (IDR) *
              </Label>
              <Input
                type="number"
                value={formData.target_budget}
                onChange={(e) => setFormData({ ...formData, target_budget: e.target.value })}
                placeholder="e.g., 500000000"
                className="h-12 bg-white border-warm-grey"
                data-testid="budget-input"
              />
            </div>

            {/* Event Category & Status */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="font-body text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Event Category *
                </Label>
                <Select 
                  value={formData.event_category} 
                  onValueChange={(value) => setFormData({ ...formData, event_category: value })}
                >
                  <SelectTrigger className="h-12 bg-white border-warm-grey" data-testid="event-category-select">
                    <SelectValue placeholder="Select event category" />
                  </SelectTrigger>
                  <SelectContent>
                    {EVENT_CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>
                        <div>
                          <span className="font-medium">{cat.label}</span>
                          <p className="text-xs text-muted-foreground">{cat.description}</p>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  {formData.event_category === 'Wedding Planner' 
                    ? 'Client will have view-only access. Leader manages all data.'
                    : 'Client can add and edit items (but cannot delete).'}
                </p>
              </div>
              <div className="space-y-2">
                <Label className="font-body text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Project Status *
                </Label>
                <Select 
                  value={formData.status.toString()} 
                  onValueChange={(value) => setFormData({ ...formData, status: parseInt(value) })}
                >
                  <SelectTrigger className="h-12 bg-white border-warm-grey" data-testid="project-status-select">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROJECT_STATUSES.map(s => (
                      <SelectItem key={s.value} value={s.value.toString()}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label className="font-body text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Notes
              </Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes about the project..."
                className="min-h-24 bg-white border-warm-grey"
                data-testid="notes-input"
              />
            </div>

            {/* Submit */}
            <div className="flex gap-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/projects')}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 bg-imperial hover:bg-imperial/90"
                data-testid="submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {isEdit ? 'Update Project' : 'Create Project'}
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProjectFormPage;
